import React, { useState } from 'react';
import { Box, Typography, Button, List, ListItem } from '@mui/material';
import { makeStyles } from '@mui/styles';
import AddIcon from '@mui/icons-material/Add';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import { useSharedStyles } from '../style';
import NewDiscussionDialog from './NewDiscussionDialog';
import KenSelect from '../../../../../../../GlobalComponents/KenSelect';
import { CourseSf } from '../../../../../../../interfaces/Common';
import { TopicWithCourse } from '../types';

interface DiscussionLeftPanelProps {
    topics: TopicWithCourse[];
    loading: boolean;
    selectedTopicId: string | null;
    onTopicClick: (topicId: string) => void;
    trainingOptions: { label: string; value: string }[];
    selectedCourseName: string;
    onCourseNameChange: (courseName: string) => void;
    batchOptions: { label: string; value: string }[];
    selectedBatch: string;
    onBatchChange: (batchId: string) => void;
    // Full course list (Training + Batch, unfiltered by the page's own
    // filters) — "Post topic" gets its own independent Training→Batch
    // cascade so posting isn't limited to whatever the page happens to be
    // filtered to.
    courses: CourseSf[];
    postToBatchId: string | undefined;
    onTopicCreated: () => void;
    currentUserName: string | undefined;
    showCourseTag: boolean;
}

const useStyles = makeStyles(() => ({
    card: {
        backgroundColor: '#fff',
        border: '1px solid #EEF1F5',
        borderRadius: '12px !important',
        boxShadow: '0 8px 24px rgba(31, 65, 114, 0.03)',
        padding: '20px',
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: '16px',
        marginBottom: '16px',
        flexWrap: 'wrap',
    },
    headerActions: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
    },
    batchSelect: {
        minWidth: '200px',
    },
    discussionItem: {
        padding: '15px',
        cursor: 'pointer',
        borderRadius: '10px',
        display: 'flex',
        alignItems: 'flex-start',
        gap: '12px',
        border: '1px solid transparent',
        '&:hover': {
            backgroundColor: '#F9FBFD',
        },
    },
    selectedDiscussionItem: {
        backgroundColor: '#F5FAFF',
        border: '1px solid #1687D0',
        borderLeft: '4px solid #1687D0',
        paddingLeft: '12px',
    },
    iconBadge: {
        width: '32px',
        height: '32px',
        borderRadius: '8px',
        background: '#E8F5EC',
        color: '#2E9E52',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        marginTop: '2px',
    },
    metaRow: {
        fontSize: '12.5px',
        color: '#8A94A4',
        marginTop: '2px',
    },
    rateBadge: {
        fontSize: '12.5px',
        fontWeight: 600,
        color: '#8A6116',
        background: '#FFF3D9',
        borderRadius: '16px',
        padding: '5px 12px',
        whiteSpace: 'nowrap',
    },
}));

const DiscussionLeftPanel: React.FC<DiscussionLeftPanelProps> = ({
    topics,
    loading,
    selectedTopicId,
    onTopicClick,
    trainingOptions,
    selectedCourseName,
    onCourseNameChange,
    batchOptions,
    selectedBatch,
    onBatchChange,
    courses,
    postToBatchId,
    onTopicCreated,
    currentUserName,
    showCourseTag,
}) => {
    const classes = useStyles();
    const sharedClasses = useSharedStyles();
    const [postTopicOpen, setPostTopicOpen] = useState(false);

    const unratedCount = (topic: TopicWithCourse) =>
        topic.comments.filter((c) => c.rating === undefined || c.rating === null).length;

    return (
        <Box className={classes.card}>
            <Box className={classes.header}>
                <Typography className={sharedClasses.font16500} align="left">
                    Discussion topics
                </Typography>
                <Box className={classes.headerActions}>
                    <Box className={classes.batchSelect}>
                        <KenSelect
                            selectLabel="Select training"
                            options={trainingOptions}
                            value={selectedCourseName}
                            handleChange={(e: any) => onCourseNameChange(e.target.value)}
                            textFieldProps={{
                                sx: {
                                    marginTop: 0,
                                    '& .MuiOutlinedInput-root': {
                                        height: '48px',
                                        borderRadius: '10px',
                                        fontSize: '14px',
                                    },
                                },
                            }}
                        />
                    </Box>
                    <Box className={classes.batchSelect}>
                        <KenSelect
                            selectLabel="All batches"
                            options={batchOptions}
                            value={selectedBatch}
                            handleChange={(e: any) => onBatchChange(e.target.value)}
                            textFieldProps={{
                                sx: {
                                    marginTop: 0,
                                    '& .MuiOutlinedInput-root': {
                                        height: '48px',
                                        borderRadius: '10px',
                                        fontSize: '14px',
                                    },
                                },
                            }}
                        />
                    </Box>
                    <Button
                        startIcon={<AddIcon fontSize="small" />}
                        onClick={() => setPostTopicOpen(true)}
                        disabled={!postToBatchId}
                        title={!postToBatchId ? 'Select a batch to post a topic' : undefined}
                        sx={{
                            background: '#1B8BCB !important',
                            color: '#fff !important',
                            borderRadius: '10px !important',
                            height: '48px',
                            textTransform: 'none',
                            fontSize: '14px',
                            fontWeight: 600,
                            padding: '0 22px',
                            whiteSpace: 'nowrap',
                            boxShadow: '0 5px 14px rgba(27, 139, 203, 0.18)',
                            '&:hover': { background: '#147DB9 !important' },
                            '&.Mui-disabled': {
                                background: '#E5EAF1 !important',
                                color: '#9AA5B4 !important',
                                boxShadow: 'none',
                            },
                        }}
                    >
                        Post topic
                    </Button>
                </Box>
            </Box>

            {loading ? (
                <Box p={2}>
                    <Typography variant="body2" color="textSecondary" align="left">
                        Loading discussions...
                    </Typography>
                </Box>
            ) : (
                <List disablePadding>
                    {topics.length > 0 ? (
                        topics.map((topic) => {
                            const toRate = unratedCount(topic);
                            const createdBy = topic.postedByName === currentUserName || !topic.postedByName
                                ? 'You'
                                : topic.postedByName;
                            return (
                                <ListItem
                                    key={topic.topicId}
                                    className={`${classes.discussionItem} ${selectedTopicId === topic.topicId ? classes.selectedDiscussionItem : ''}`}
                                    onClick={() => onTopicClick(topic.topicId)}
                                >
                                    <Box className={classes.iconBadge}>
                                        <ChatBubbleOutlineIcon sx={{ fontSize: 18 }} />
                                    </Box>
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography sx={{ fontWeight: 600, fontSize: '14.5px' }} align="left">
                                            {topic.question}
                                        </Typography>
                                        <Typography className={classes.metaRow} align="left">
                                            {showCourseTag && topic.courseLabel ? `${topic.courseLabel} · ` : ''}
                                            {topic.totalComments} {topic.totalComments === 1 ? 'response' : 'responses'} · created by {createdBy}
                                        </Typography>
                                    </Box>
                                    {toRate > 0 && (
                                        <Box className={classes.rateBadge}>{toRate} to rate</Box>
                                    )}
                                </ListItem>
                            );
                        })
                    ) : (
                        <Box p={2}>
                            <Typography variant="body2" color="textSecondary" align="left">
                                No discussions available
                            </Typography>
                        </Box>
                    )}
                </List>
            )}

            {postToBatchId && (
                <NewDiscussionDialog
                    open={postTopicOpen}
                    batchId={postToBatchId}
                    courses={courses}
                    onClose={() => setPostTopicOpen(false)}
                    onSuccess={() => {
                        setPostTopicOpen(false);
                        onTopicCreated();
                    }}
                />
            )}
        </Box>
    );
};

export default DiscussionLeftPanel;
